/**
 * ResultInterpretationCard — Auto-shown after first result for complex outputs.
 *
 * Appears below the result for Pattern Scan, PageRank, Community Detection etc.
 * Dismissed once → gone forever for that feature (localStorage).
 */
import React, { useState } from 'react';
import { Lightbulb, X } from 'lucide-react';
import { clsx } from 'clsx';

interface ResultInterpretationCardProps {
    featureKey: string;     // e.g. 'pattern-scan-results'
    title?: string;
    children: React.ReactNode;
    className?: string;
}

const STORAGE_PREFIX = 'result-interp-dismissed-';

export const ResultInterpretationCard: React.FC<ResultInterpretationCardProps> = ({
    featureKey,
    title = 'How to interpret these results',
    children,
    className,
}) => {
    const storageKey = `${STORAGE_PREFIX}${featureKey}`;

    const [isDismissed, setIsDismissed] = useState(() => {
        const globalMode = localStorage.getItem('explainerMode');
        if (globalMode === 'off') return true;
        return localStorage.getItem(storageKey) === 'true';
    });

    const handleDismiss = () => {
        setIsDismissed(true);
        localStorage.setItem(storageKey, 'true');
    };

    if (isDismissed) return null;

    return (
        <div className={clsx(
            "bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/50 rounded-lg text-xs px-3 py-2.5",
            className
        )}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                    <div>
                        <div className="font-medium text-emerald-800 dark:text-emerald-200 mb-1">{title}</div>
                        <div className="text-emerald-700 dark:text-emerald-300/80 leading-relaxed">
                            {children}
                        </div>
                    </div>
                </div>
                <button
                    onClick={handleDismiss}
                    className="p-0.5 text-emerald-500/50 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors shrink-0"
                    title="Don't show again"
                    type="button"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
};
